<template>
    <div class="productivity-container">

        <div class="productivity-header-row">
            <div class="productivity-cell" v-for="cell of cells"
                 :key="cell.dateKey">
                <div class="productivity-label-avg bold" :style="{fontSize: cell.labelSize}"
                     @mouseover="$emit('cell-mouse-over', $event, cell.dateKey)"
                     @mouseleave="$emit('cell-mouse-leave', $event, cell.dateKey)">
                    {{ cell.title }}
                </div>
                <div class="productivity-avg-indicator" v-on:click="toggleExpanded" :style="{backgroundColor: cell.boxColor}"></div>
            </div>
        </div>

        <div class="productivity-table-container" :class="{expanded: expanded}" @scroll="onTableScroll">
            <div class="table-left">
                <div class="actions">
                    <b>{{ $t('roster.comp.monthlyAverage') }}:&nbsp;{{ averageMonthProductivity.toFixed(0) }}</b>
                </div>
                <div class="time-labels" ref="timeLabels">
                    <div class="time-label" v-for="timeLabel of timeLabels">{{ timeLabel }}</div>
                </div>
            </div>
            <div class="productivity-table" v-if="!loading">
                <div class="productivity-row" v-for="row of productivityRows"> 
                    <div class="productivity-cell half" v-for="cell of row.cells"
                         :style="{backgroundColor: cell.boxColor}">
                        <div class="productivity-label">
                            {{ cell.title }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="loading-container" v-else
                 :style="{ width: loadingContainerWidth, height: loadingContainerHeight }">
                <div class="fas fa-spinner fa-spin"></div>
            </div>
        </div>

    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { addDays, differenceInDays, format, formatISO, parse, parseISO, startOfDay } from "date-fns";
import Pdhelper from "@/components/uicomponents/charts/pdhelper";

export default {
    name: "ProductivityPopup",
    props: {
        // mode: {
        //     type: String,
        //     default: 'month'
        // },
        expanded: false,
        displayIndicatorType: {
            type: String,
            default: null
        },
        productivitiesDaily: {
            type: Object,
            default: () => ( {} ),
        },
        roundProductivity: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedRow: null,
            loading: false,
            planningIndicatorsHourly: null,
            hourlyProductivityMap: {},
        }
    },
    mounted() {
        if (this.expanded) {
            this.loading = true;
            this.loadAllPlanningIndicators(this.displayIndicatorType)
                .then((planningIndicatorsHourly) => {
                    this.planningIndicatorsHourly = planningIndicatorsHourly;
                    this.hourlyProductivityMap = this.calculateHourlyProductivityMap();
                    this.loading = false;
                });
        }
    },
    watch: {
        expanded() {
            if (this.expanded && this.planningIndicatorsHourly === null) {
                this.loading = true;
                this.loadAllPlanningIndicators(this.displayIndicatorType)
                    .then((planningIndicatorsHourly) => {
                        this.planningIndicatorsHourly = planningIndicatorsHourly;
                        this.hourlyProductivityMap = this.calculateHourlyProductivityMap();
                        this.loading = false;
                    });
            }
        },
        rosterFromTo() {
            this.planningIndicatorsHourly = null;
        },
        rosterVisibilityState: {
            deep: true,
            handler() {
                this.hourlyProductivityMap = {};
                this.loading = true;
                setTimeout(() => {
                    this.hourlyProductivityMap = this.calculateHourlyProductivityMap();
                    this.loading = false;
                }, 0);
            },
        },
    },
    computed: {
        ...mapGetters([
            'rosterFromTo',
            'rosterVisibilityState',
            'shiftTimeByDayMap',
            'planningIndicatorTypeMap',
            'planShiftMap',
            'actualShiftMap',
            'actualBreakMap',
            'storeSettings',
            'spotMap',
            'employeeMap',
        ]),

        startDate() {
            return this.rosterVisibilityState.visibilityMonthOnly
                ? new Date(this.rosterFromTo.year, this.rosterFromTo.month, 1)
                : parseISO(this.rosterFromTo.from);
        },

        endDate() {
            return this.rosterVisibilityState.visibilityMonthOnly
                ? new Date(this.rosterFromTo.year, this.rosterFromTo.month + 1, 1)
                : parseISO(this.rosterFromTo.to);
        },

        productivityMap() {
            const productivityMap = {};

            for (const [ dateKey, mapEntry ] of Object.entries(this.shiftTimeByDayMap)) {

                let workTimeMS = 0;

                let amountActual = this.productivitiesDaily[dateKey] ? this.productivitiesDaily[dateKey].amountActual : 0;
                const mode = ( mapEntry.productiveActual > 0 && amountActual > 0 ) ? 2 : 1;

                if (mode === 1) {
                    workTimeMS = mapEntry.productiveAssigned + mapEntry.productiveAssignedPreviousDay;
                } else if (mode === 2) {
                    workTimeMS = mapEntry.productiveActual + mapEntry.productiveActualPreviousDay;
                }
                if (!this.rosterVisibilityState.visibilityProductivityIncludeMgmt) {
                    if (mode === 1) {
                        workTimeMS = mapEntry.productiveAssignedCrewOnly + mapEntry.productiveAssignedPreviousDayCrewOnly;
                    } else if (mode === 2) {
                        workTimeMS = mapEntry.productiveActualCrewOnly + mapEntry.productiveActualPreviousDayCrewOnly;
                    }
                }

                let workTime = ( workTimeMS ) / 1000 / 60 / 60;
                let prods = this.productivitiesDaily[dateKey];
                let fractional = this.displayIndicatorType ? ( this.planningIndicatorTypeMap[this.displayIndicatorType] ? this.planningIndicatorTypeMap[this.displayIndicatorType].fractional : 7 ) : 7
                let divisor = Math.pow(10, fractional);

                if (prods) {
                    let amount = 0;
                    switch (mode) {
                        case 1: // planned
                            amount = prods.amountPlanned / divisor;
                            break;
                        case 2: // actual
                            amount = prods.amountActual / divisor;
                            break;
                    }
                    if (workTime > 0) {
                        productivityMap[dateKey] = ( amount / workTime );
                    } else {
                        productivityMap[dateKey] = null;
                    }

                }
            }

            return productivityMap;
        },
        cells() {
            const cells = [];

            let sum = 0;
            let count = 0;

            for (let day = this.startDate; day < this.endDate; day = addDays(day, 1)) {
                const dateKey = format(day, 'yyyy-MM-dd');
                const productivity = this.productivityMap[dateKey];
                if (productivity) {
                    sum += productivity;
                    count++;
                }
            }

            const prodAvg = sum / count;

            for (let day = this.startDate; day < this.endDate; day = addDays(day, 1)) {
                const dateKey = format(day, 'yyyy-MM-dd');
                // const title = `${day.getDate()}`;

                let backgroundColor = 'var(--bg)';

                if (day.getDay() === 0 || day.getDay() === 6) {
                    backgroundColor = 'var(--weekend-bg)';
                }

                const productivity = this.productivityMap[dateKey]
                    ? this.productivityMap[dateKey].toFixed(this.roundProductivity ? 0 : 2)
                    : null;

                let diff = 0;

                if (productivity && prodAvg)
                    diff = Math.abs(productivity - prodAvg) / prodAvg;

                let boxColor = '#6d8d23';
                if(productivity == null){
                    boxColor = 'var(--contrast-7)';
                }else{
                    boxColor = this.getHeatmapColor(diff, true);
                }

                /* let boxColor = '#6d8d23';
                if (diff > 0.5) {
                    boxColor = '#DF3740';
                } else if (diff > 0.25) {
                    boxColor = '#E26E17';
                } else if (diff > 0.125) {
                    boxColor = '#E4C524';
                } */

                const title = `${productivity || '--'}`;

                let labelSize = this.roundProductivity ? 0.75 : 0.65;
                /* if (title.length > 5) {
                    for (let i = 0; i < title.length - 4; i++) {
                        labelSize = labelSize * 0.8;
                    }
                } */

                labelSize = `${labelSize}rem`;

                cells.push({
                    dateKey,
                    title,
                    backgroundColor,
                    boxColor,
                    productivity,
                    labelSize,
                });
            }

            return cells;
        },

        timeLabels() {
            const labels = [];

            for (let hour = 0; hour < 24; hour++) {
                labels.push(`${hour.toString().padStart(2, '0')}:00`);
            }

            return labels;
        },

        loadingContainerWidth() {
            const dayCount = differenceInDays(this.endDate, this.startDate);
            return `${dayCount * 3.2075 + 1}rem`;
        },

        loadingContainerHeight() {
            return `${this.timeLabels.length * 2 + 0.75}rem`;
        },
        averageMonthProductivity(){
            let sum = 0;
            let count = 0;
            for (let day = this.startDate; day < this.endDate; day = addDays(day, 1)) {
                const dateKey = format(day, 'yyyy-MM-dd');
                const productivity = this.productivityMap[dateKey];
                if (productivity) {
                    sum += productivity;
                    count++;
                }
            }

            const prodAvg = sum / count;
            return prodAvg;
        },

        productivityRows() {
            const rows = [];
            
            for (let hour = 0; hour < 24; hour++) {
                let hourPadded = hour.toString().padStart(2, '0');
                const row = {
                    hour,
                    cells: [],
                };

                for (let day = this.startDate; day < this.endDate; day = addDays(day, 1)) {
                    const dateKey = format(day, 'yyyy-MM-dd');

                    let backgroundColor = 'var(--bg)';
                    if (day.getDay() === 0 || day.getDay() === 6) {
                        backgroundColor = 'var(--weekend-bg)';
                    }

                    const productivity = this.hourlyProductivityMap[dateKey + ' ' + hourPadded] || {
                        planned: null,
                        actual: null,
                    };

                    // TODO: Determine which productivity to use
                    let determinedProductivity = productivity.actual || productivity.planned;

                    let diff = 0;

                    let dayProdAvg = this.productivityMap[dateKey];
                    const avgMonthProd = this.averageMonthProductivity;
                    if (avgMonthProd && determinedProductivity) {
                        diff = (determinedProductivity - avgMonthProd) / avgMonthProd;
                    }

                    const title = determinedProductivity
                        ? `${determinedProductivity.toFixed(this.roundProductivity ? 0 : 2) || '--'}`
                        : '--';
                    let boxColor = '#6d8d23';
                    if(determinedProductivity == null){
                        boxColor = 'var(--contrast-7)';
                    }else{
                        boxColor = this.getHeatmapColor(diff);
                    }

                    row.cells.push({
                        dateKey,
                        title,
                        backgroundColor,
                        boxColor,
                        productivity,
                    });
                }

                rows.push(row);

                // TODO: if (hour === this.selectedHour)
            }

            return rows;
        },

    },
    methods: {
        toggleExpanded(){
            this.$parent.productivityExpanded = !this.expanded;
            //this.$forceUpdate();
        },
        getHeatmapColor(deviation, forAvgValue = false) {
            // Define thresholds and colors
            const thresholds = forAvgValue? [0, 0.125, 0.25, 0.5] : [0, 0.25, 0.5, 0.75];
            const colors = ['#6d8d23', '#E4C524', '#E26E17', '#DF3740'];
            let fadeToGrey = false;
            if(deviation < 0){
                fadeToGrey = true;
                deviation = Math.abs(deviation);
            }
            // Normalize deviation to the range of 0 to 1
            deviation = Math.min(Math.max(deviation, 0), 1);

            if(fadeToGrey){
                return this.interpolateColor('#6d8d23', '#808080', deviation);
            }
            // Find the appropriate color range
            for (let i = 0; i < thresholds.length - 1; i++) {
                if (deviation <= thresholds[i + 1]) {
                    const rangeFactor = (deviation - thresholds[i]) / (thresholds[i + 1] - thresholds[i]);
                    return this.interpolateColor(colors[i], colors[i + 1], rangeFactor);
                }
            }

            // Return the last color (red) if deviation is >= 0.5
            return colors[colors.length - 1];
        },
        interpolateColor(color1, color2, factor) {
            // Convert hex color to RGB
            const c1 = parseInt(color1.substring(1), 16);
            const c2 = parseInt(color2.substring(1), 16);

            const r1 = (c1 >> 16) & 0xFF, g1 = (c1 >> 8) & 0xFF, b1 = c1 & 0xFF;
            const r2 = (c2 >> 16) & 0xFF, g2 = (c2 >> 8) & 0xFF, b2 = c2 & 0xFF;

            // Interpolate RGB values
            const r = Math.round(r1 + (r2 - r1) * factor);
            const g = Math.round(g1 + (g2 - g1) * factor);
            const b = Math.round(b1 + (b2 - b1) * factor);

            // Convert RGB back to hex
            return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`;
        },
        async loadPlanningIndicatorGroupedByHour(date, typeId = null) {
            const url = '/api/sec/planningindicator/grouped/hour';

            const params = {
                storeId: this.$store.state.storeId,
                from: formatISO(startOfDay(date)),
                to: formatISO(startOfDay(addDays(date, 1))),
            }

            if (typeId) {
                params.typeId = typeId;
            }

            const response = await this.axios({
                method: 'get',
                params: params,
                headers: {
                    'AUTHORIZATION': "bearer " + this.$store.state.jwt
                },
                url: url
            });

            return response.data;
        },

        async loadAllPlanningIndicators(typeId = null) {
            const planningIndicators = {};

            for (let day = this.startDate; day < this.endDate; day = addDays(day, 1)) {
                const dateKey = format(day, 'yyyy-MM-dd');
                const planningIndicatorsHourly = await this.loadPlanningIndicatorGroupedByHour(day, typeId);
                planningIndicators[dateKey] = planningIndicatorsHourly;
            }

            return planningIndicators;
        },

        convertIndicatorsToFractions(indicatorMap) {
            const convertedIndicatorMap = {};

            for (const [ dateKey, indicators ] of Object.entries(indicatorMap)) {
                for (const indicator of indicators) {
                    const d = new Date(indicator.date);
                    const time = format(d, 'HH');
                    const type = this.planningIndicatorTypeMap[indicator.typeId];
                    const totalPlanned = convertedIndicatorMap[dateKey + ' ' + time]
                        ? convertedIndicatorMap[dateKey + ' ' + time].plannedTotal
                        : 0;
                    const totalActual = convertedIndicatorMap[dateKey + ' ' + time]
                        ? convertedIndicatorMap[dateKey + ' ' + time].actualTotal
                        : 0;
                    let actual = indicator.planned ? 0 : indicator.sumAmount;
                    let planned = indicator.planned ? indicator.sumAmount : 0;
                    if (type) {
                        actual = actual / Math.pow(10, type.fractional);
                        planned = planned / Math.pow(10, type.fractional);
                    }
                    convertedIndicatorMap[dateKey + ' ' + time] = {
                        plannedTotal: totalPlanned + planned,
                        actualTotal: totalActual + actual,
                    };
                }
            }

            return convertedIndicatorMap;
        },

        calculateHourlyProductivityMap() {
            if (!this.planningIndicatorsHourly) {
                return {};
            }

            const pdHelper = new Pdhelper(this.storeSettings);

            const indicators = this.planningIndicatorsHourly;

            const employeeIds = pdHelper.filterEmployeeIds(
                this.employeeMap,
                this.rosterVisibilityState.visibilityProductivityIncludeMgmt ? [ 1, 2 ] : [ 1 ],
                this.spotMap,
            );

            const from = pdHelper.moment(this.startDate);
            const to = pdHelper.moment(this.endDate);
            const indicatorMap = this.convertIndicatorsToFractions(indicators);
            const planShiftMap = pdHelper.filterShifts(Object.values(this.planShiftMap), from, to, 'day', employeeIds);
            const actualShifts = pdHelper.combineActualShiftBreaks(this.actualShiftMap, this.actualBreakMap);
            const actualShiftMap = pdHelper.filterShifts(actualShifts, from, to, 'day', employeeIds);

            const productiveWT = pdHelper.calculateProductiveWorkTime(planShiftMap, actualShiftMap, 'day', from, to, employeeIds, this.spotMap);
            const productivityMap = pdHelper.calculateProductivity(productiveWT, indicatorMap, 'day');

            return productivityMap;
        },

        onTableScroll(event) {
            if (event.target.scrollTop > 0) {
                this.$refs.timeLabels.style.marginTop = `${-event.target.scrollTop}px`;
            } else {
                this.$refs.timeLabels.style.marginTop = '0';
            }
        },
    },
}
</script>

<style scoped>
.productivity-container {
    position: fixed;
    top: 90px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    align-content: center;
    box-sizing: border-box;
    z-index: 1;
}

.productivity-header-row {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
}

.productivity-cell {
    width: 3.205rem;
    height: 1.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border: solid 0.5px var(--bg);
    align-items: center;
    margin-top: -0.3rem;
}

.productivity-avg-indicator {
    height: 0.5rem;
    width: 100%;
    border-radius: 0.25rem;
    color: var(--bg);
    cursor: pointer;
}

.productivity-label {
    width: 2.5rem;
    aspect-ratio: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-family: DistrictPro, sans-serif;
    font-size: 0.75rem;
    color: white;
    transition: transform 0.2s ease;
}

.productivity-label-avg{
    color: var(--contrast-4);
    font-family: DistrictProBold, sans-serif;
}

.productivity-label.bold {
    font-family: DistrictProBold, sans-serif;
}

.productivity-label:hover {
    font-family: DistrictProBold, sans-serif;
    transform: scale(1.13);
    
}

.productivity-cell.half:hover {
    transform: scale(1.13);
    border: solid 1px var(--bg);
    z-index: 999;
    
}

.productivity-table-container {
    margin-top: 22px;
    position: absolute;
    left: 0;
    right: 0;
    background-color: var(--bg);
    max-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    transition: max-height 250ms ease-in-out;
    z-index: 2;
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.5);
}

.productivity-table-container.expanded {
    max-height: calc(90vh - 200px);
    -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

.productivity-table-container.expanded::-webkit-scrollbar {
  display: none;
}


.table-left {
    --box-shadow-offset: 0;
    position: fixed;
    left: 0;
    margin-top: -4px;
    width: 260px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    align-content: flex-start;
    background-color: var(--bg);
    transition: max-height 250ms ease-in-out;
    overflow-y: hidden;
    z-index: 1;
    max-height: 0;
    box-shadow: calc(-5px + var(--box-shadow-offset) * 1px) 5px 10px 0 rgba(0, 0, 0, 0.5);
}

.productivity-table-container.expanded > .table-left {
    padding-bottom: 4px;
    max-height: calc(90vh - 196px);
}

.table-left > .actions {
    width: 100%;
    height: 3rem;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    align-content: center;
    font-family: DistrictPro, sans-serif;
    font-size: 0.85rem;
    padding: 12px 12px;
}

.time-labels {
    flex: 1;
}

.time-label {
    width: 100%;
    height: 2rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
    font-family: DistrictProBold, sans-serif;
    color: var(--contrast-7);
    font-size: 0.8rem;
    padding-left: 1rem;
    padding-top: 1rem;
    padding-right: 0.5rem;
}

.time-label:last-child{
    margin-bottom: 6px;
}

.loading-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-height: calc(90vh - 200px);
}

.loading-container > .fa-spinner {
    font-size: 2.5rem;
    color: var(--md);
}

.productivity-table {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    height: 100%;
    width: calc(100%);
    overflow: hidden;
}

.productivity-row {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    align-content: center;
    width: 100%;
    height: 2rem;
}

.productivity-row:last-child {
    margin-bottom: 6px;
}

/* .productivity-row:hover:after {
    content: '';
    position: absolute;
    left: -4rem;
    right: 0;
    top: 0;
    bottom: 0;
    background-color: var(--contrast-2);
    z-index: 1;
    mix-blend-mode: multiply;
} */

.productivity-cell.half {
    width: 3.205rem;
    height: 2rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0.125rem;
    transition: transform 0.2s ease;
}
</style>

<style>
.productivity-container {
    --weekend-bg: var(--contrast-2);
}

.nightmode .productivity-container {
    --weekend-bg: #333;
}
</style>
